import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import GoogleAddressInput from "@/components/GoogleAddressInput";

/**
 * Manual lead entry — for phone-call signups, walk-ins, and neighbor
 * referrals the office wants in the pipeline without asking the customer
 * to go fill out the widget.
 */
export function ManualLeadDialog({ open, onOpenChange, onCreated }) {
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        name: "", email: "", phone: "",
        line1: "", line2: "", city: "", state: "TX", zip: "",
        cart_quantity: 1, cart_size: "96gal", preferred_day: "",
        notes: "",
    });
    const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));
    const reset = () => setForm({
        name: "", email: "", phone: "",
        line1: "", line2: "", city: "", state: "TX", zip: "",
        cart_quantity: 1, cart_size: "96gal", preferred_day: "",
        notes: "",
    });

    const canSave = form.name.trim() && form.email.trim()
        && form.phone.trim() && form.line1.trim() && form.city.trim();

    const submit = async () => {
        if (!canSave) return;
        setSaving(true);
        try {
            const body = {
                name: form.name,
                email: form.email,
                phone: form.phone,
                address: {
                    line1: form.line1, line2: form.line2,
                    city: form.city, state: form.state, zip: form.zip,
                },
                cart_quantity: Number(form.cart_quantity) || 1,
                cart_size: form.cart_size,
                preferred_day: form.preferred_day || null,
                notes: form.notes,
            };
            await api.post("/leads", body);
            toast.success(`Lead added — ${form.name}`);
            reset();
            onOpenChange(false);
            onCreated?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to add lead");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="w-full sm:max-w-lg overflow-y-auto"
                          data-testid="manual-lead-dialog">
                <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                        <UserPlus className="w-4 h-4" />Add lead manually
                    </SheetTitle>
                    <SheetDescription>
                        For phone calls, walk-ins, and referrals. Same fields as the
                        public signup widget.
                    </SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-3">
                    <Field label="Name">
                        <Input value={form.name} onChange={(e) => set("name", e.target.value)}
                               data-testid="manual-lead-name" />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Email">
                            <Input type="email" value={form.email}
                                   onChange={(e) => set("email", e.target.value)}
                                   data-testid="manual-lead-email" />
                        </Field>
                        <Field label="Phone">
                            <Input value={form.phone}
                                   onChange={(e) => set("phone", e.target.value)}
                                   placeholder="+1 555 555 5555"
                                   data-testid="manual-lead-phone" />
                        </Field>
                    </div>

                    <Field label="Address line 1">
                        <GoogleAddressInput
                            value={form.line1}
                            onChange={(v) => set("line1", v)}
                            onAddressSelected={(a) => setForm((c) => ({
                                ...c,
                                line1: a.line1 || c.line1,
                                city: a.city || c.city,
                                state: a.state || c.state,
                                zip: a.zip || c.zip,
                            }))}
                            placeholder="Start typing an address..."
                            testid="manual-lead-line1"
                        />
                    </Field>
                    <Field label="Address line 2 (optional)">
                        <Input value={form.line2}
                               onChange={(e) => set("line2", e.target.value)} />
                    </Field>
                    <div className="grid grid-cols-3 gap-3">
                        <Field label="City">
                            <Input value={form.city}
                                   onChange={(e) => set("city", e.target.value)} />
                        </Field>
                        <Field label="State">
                            <Input value={form.state}
                                   onChange={(e) => set("state", e.target.value)} />
                        </Field>
                        <Field label="ZIP">
                            <Input value={form.zip}
                                   onChange={(e) => set("zip", e.target.value)} />
                        </Field>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        <Field label="Carts">
                            <Input type="number" min={1} max={6}
                                   value={form.cart_quantity}
                                   onChange={(e) => set("cart_quantity", e.target.value)} />
                        </Field>
                        <Field label="Cart size">
                            <Select value={form.cart_size}
                                    onValueChange={(v) => set("cart_size", v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="35gal">35 gal</SelectItem>
                                    <SelectItem value="64gal">64 gal</SelectItem>
                                    <SelectItem value="96gal">96 gal</SelectItem>
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Preferred day">
                            <Select value={form.preferred_day || "any"}
                                    onValueChange={(v) => set("preferred_day",
                                                              v === "any" ? "" : v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="any">No preference</SelectItem>
                                    <SelectItem value="monday">Monday</SelectItem>
                                    <SelectItem value="tuesday">Tuesday</SelectItem>
                                    <SelectItem value="wednesday">Wednesday</SelectItem>
                                    <SelectItem value="thursday">Thursday</SelectItem>
                                    <SelectItem value="friday">Friday</SelectItem>
                                    <SelectItem value="saturday">Saturday</SelectItem>
                                </SelectContent>
                            </Select>
                        </Field>
                    </div>

                    <Field label="Notes">
                        <Textarea rows={2} value={form.notes}
                                  onChange={(e) => set("notes", e.target.value)}
                                  placeholder="Referred by ..." />
                    </Field>

                    <div className="flex justify-end gap-2 pt-4 border-t border-border">
                        <Button variant="outline" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button onClick={submit} disabled={!canSave || saving}
                                data-testid="manual-lead-submit">
                            {saving ? "Adding..." : "Add lead"}
                        </Button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                {label}
            </Label>
            {children}
        </div>
    );
}
